'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { AddressAutocomplete } from '@/components/address/AddressAutocomplete';
import type { AddressSuggestion } from '@/hooks/useAddressSearch';
import { useAriaLabels } from '@/hooks/useAriaLabels';

import styles from './AddressFinder.module.scss';

interface AddressFinderProps {
  /** Country code for search context (e.g., 'US', 'GB'). Defaults to 'US' when not provided. */
  countryCode?: string;
  /** Callback when address is selected */
  onAddressSelected: (address: AddressSuggestion) => void;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Placeholder text */
  placeholder?: string;
}

/**
 * Account address-book wrapper around the shared Melissa autocomplete.
 */
export function AddressFinder({
  countryCode,
  onAddressSelected,
  disabled = false,
  placeholder,
}: Readonly<AddressFinderProps>) {
  const t = useTranslations('myAccount.addresses');
  const aria = useAriaLabels();
  const resolvedPlaceholder = placeholder || t('addressPlaceholder');
  const [inputValue, setInputValue] = useState('');

  return (
    <div className={styles.wrapper}>
      <AddressAutocomplete
        value={inputValue}
        onChange={setInputValue}
        onSelect={() => undefined}
        onSelectSuggestion={onAddressSelected}
        countryCode={countryCode}
        label={t('findYourAddress')}
        hint={t('noShipPoBox')}
        placeholder={resolvedPlaceholder}
        disabled={disabled}
        fillInputOnSelect="displayText"
        maxRecords={10}
        className={styles.autocomplete}
        dataTestId="AddressFinder"
        listboxAriaLabel={aria.action('addressSuggestions')}
        loadingLabel={aria.action('loadingSuggestions')}
        emptyLabel={t('noAddressesFound')}
      />
    </div>
  );
}

AddressFinder.displayName = 'AddressFinder';
